/**
 * StudyDetail — the per-study workspace (Study Hub, Phase A).
 *
 * Tabs: Overview (AI summary + stipend), Script (recruiter phone script),
 * Prescreen (AI-drafted criteria -> human review -> immutable publish),
 * Documents (protocol/ICF uploads + extraction status).
 *
 * While anything is processing (document extraction or artifact generation)
 * the page polls the detail endpoint every 4s so status chips flip without a
 * manual refresh. Editing surfaces are admin-gated (useIsCRMAdmin); recruiters
 * get a read-only view of published assets.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, ClipboardCheck, FileText, FlaskConical, Link2, Pencil, RefreshCw, Upload } from 'lucide-react';
import api from '../../../services/api';
import { StudyDetailResponse, studyInternalLabel } from '../../types';
import { studyStatusMeta, STUDY_STATUSES, useIsCRMAdmin } from '../../lib';
import LinkRegistryModal from '../../components/studies/LinkRegistryModal';
import OverviewTab from '../../components/studies/OverviewTab';
import ScriptTab from '../../components/studies/ScriptTab';
import PrescreenTab from '../../components/studies/PrescreenTab';
import DocumentsTab from '../../components/studies/DocumentsTab';
import FormsTab from '../../components/studies/FormsTab';
import TemplatesTab from '../../components/studies/TemplatesTab';
import StudyFlowsTab from '../../components/studies/StudyFlowsTab';
import WebsiteTab from '../../components/studies/WebsiteTab';
import RecruitersTab from '../../components/studies/RecruitersTab';
import ScheduleRequirementsTab from '../../components/studies/ScheduleRequirementsTab';
import PerformanceTab from '../../components/studies/PerformanceTab';
import SetupChecklist, { SetupProgress } from '../../components/studies/SetupChecklist';
import EditStudyModal from '../../components/studies/EditStudyModal';
import ImportLeadsModal from '../../components/ImportLeadsModal';
import ReviewCriteriaPanel from '../../components/eligibility/ReviewCriteriaPanel';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'script', label: 'Script' },
  { key: 'prescreen', label: 'Prescreen' },
  { key: 'forms', label: 'Forms' },
  { key: 'templates', label: 'Templates' },
  { key: 'flows', label: 'Flows' },
  { key: 'website', label: 'Website' },
  { key: 'documents', label: 'Documents' },
  { key: 'recruiters', label: 'Recruiters' },
  { key: 'scheduling', label: 'Scheduling' },
  { key: 'performance', label: 'Performance' },
] as const;

export default function StudyDetail() {
  const { id } = useParams<{ id: string }>();
  const isAdmin = useIsCRMAdmin();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === searchParams.get('tab')) ? (searchParams.get('tab') as string) : 'overview';

  const [detail, setDetail] = useState<StudyDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Setup checklist: open state is URL-driven (?setup=1, set after creation),
  // progress is reported up by the panel for the header chip, and reloadKey
  // bumps when extraction/generation settles so the chip + panel refresh.
  const setupOpen = isAdmin && searchParams.get('setup') === '1';
  const [setupProgress, setSetupProgress] = useState<SetupProgress | null>(null);
  const [setupReloadKey, setSetupReloadKey] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!id) return;
    if (!silent) setLoading(true);
    try {
      const res = await api.get(`/api/crm/studies/${id}`);
      setDetail(res.data);
      setError(null);
    } catch (err: any) {
      if (!silent) setError(err?.response?.data?.error || 'Failed to load study');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Poll while extraction/generation is in flight so chips flip on their own.
  const busy = useMemo(() => {
    if (!detail) return false;
    return (
      detail.documents.some((d) => d.extraction_status === 'pending' || d.extraction_status === 'processing') ||
      detail.artifacts.some((a) => a.status === 'generating')
    );
  }, [detail]);
  useEffect(() => {
    if (!busy) return;
    const timer = window.setInterval(() => load(true), 4000);
    return () => window.clearInterval(timer);
  }, [busy, load]);

  // When document extraction / artifact generation finishes, refresh the
  // checklist so newly-completed steps tick off without a manual reload.
  const prevBusy = useRef(busy);
  useEffect(() => {
    if (prevBusy.current && !busy) setSetupReloadKey((k) => k + 1);
    prevBusy.current = busy;
  }, [busy]);

  const openSetup = useCallback(() => setSearchParams({ tab, setup: '1' }, { replace: true }), [setSearchParams, tab]);
  const closeSetup = useCallback(() => setSearchParams({ tab }, { replace: true }), [setSearchParams, tab]);
  const gotoTab = useCallback((next: string) => setSearchParams({ tab: next }, { replace: true }), [setSearchParams]);

  const setStatus = async (status: string) => {
    if (!detail) return;
    const prev = detail.study.status;
    setDetail({ ...detail, study: { ...detail.study, status } });
    try {
      await api.patch(`/api/crm/studies/${detail.study.id}`, { status });
    } catch (err: any) {
      setDetail((d) => (d ? { ...d, study: { ...d.study, status: prev } } : d));
      window.alert(err?.response?.data?.error || 'Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
      </div>
    );
  }
  if (error || !detail) {
    return (
      <div className="mx-auto max-w-lg rounded-xl border border-red-200 bg-red-50 p-6 text-center dark:border-red-900/50 dark:bg-red-900/20">
        <p className="text-sm font-medium text-red-700 dark:text-red-300">{error || 'Study not found'}</p>
        <Link to="/crm/settings/studies" className="mt-3 inline-block text-sm font-medium text-primary-700 hover:text-primary-800 dark:text-primary-400">
          Back to studies
        </Link>
      </div>
    );
  }

  const { study } = detail;
  const meta = studyStatusMeta(study.status);
  const setupChipLabel = setupProgress?.completed
    ? 'Setup complete'
    : setupProgress
      ? `Setup ${setupProgress.done}/${setupProgress.total}`
      : 'Setup checklist';

  // Registry context (plan §5.1/§3.5): the link banner for unlinked legacy
  // studies + the informational staleness banner when eReg re-versioned a
  // source document (publish protection: information, never a nudge loop).
  const registryEnabled = detail.registry_reads_enabled !== false;
  const unlinked = registryEnabled && !study.protocol_id;
  const staleness = detail.staleness;
  const ARTIFACT_LABELS: Record<string, string> = {
    summary: 'Summary',
    recruiter_script: 'Phone script',
    prescreen_draft: 'Prescreen draft',
    stipend_info: 'Stipend info',
    website_content: 'Website content',
    message_templates: 'Message templates',
  };
  const staleItems = (() => {
    if (!staleness?.any) return [];
    // One label per artifact type ("(published)" when the live published row
    // is affected) — the backend already restricts to live rows per type.
    const byType = new Map<string, boolean>();
    for (const a of staleness.artifacts) {
      byType.set(a.artifact_type, (byType.get(a.artifact_type) || false) || a.status === 'published');
    }
    return [
      ...[...byType.entries()].map(([type, pub]) => `${ARTIFACT_LABELS[type] || type}${pub ? ' (published)' : ''}`),
      ...(staleness.prescreen ? ['Published prescreen'] : []),
      ...(staleness.criteria_sets.length ? ['AI review criteria'] : []),
    ];
  })();

  return (
    <div className="space-y-4">
      <Link
        to="/crm/settings/studies"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 transition-colors duration-150 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
      >
        <ArrowLeft className="h-4 w-4" /> Studies
      </Link>

      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <div className="rounded-lg bg-primary-50 p-2 dark:bg-primary-900/30">
              <FlaskConical className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="min-w-0">
              <h1 className="truncate text-xl font-semibold text-gray-900 dark:text-gray-100">{studyInternalLabel(study)}</h1>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
                {study.internal_title && study.internal_title !== study.title && (
                  <span title="Public title">Public: {study.title}</span>
                )}
                <span className="font-mono text-xs">{study.slug}</span>
                {study.sponsor_name && <span>{study.sponsor_name}</span>}
                {study.protocol_number && <span>#{study.protocol_number}</span>}
                <span>{study.count || 0} lead{(study.count || 0) === 1 ? '' : 's'}</span>
                {detail.prescreen && <span className="text-purple-600 dark:text-purple-400">Prescreen v{detail.prescreen.version}</span>}
                {detail.registry && (
                  <span className="inline-flex items-center gap-1 text-primary-700 dark:text-primary-400" title="Linked eReg study — documents are read from eReg">
                    <FileText className="h-3.5 w-3.5" />
                    eReg{detail.registry.protocol_number ? `: ${detail.registry.protocol_number}` : ''}
                  </span>
                )}
                {detail.registry?.archived_at && (
                  <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                    Archived in eReg
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {isAdmin && (
              <button
                type="button"
                onClick={() => setImportOpen(true)}
                aria-label="Import leads"
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 dark:focus:ring-offset-gray-800 sm:min-h-0"
              >
                <Upload className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                Import leads
              </button>
            )}
            {isAdmin && (
              <button
                type="button"
                onClick={() => setEditOpen(true)}
                aria-label="Edit study"
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 dark:focus:ring-offset-gray-800 sm:min-h-0"
              >
                <Pencil className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                Edit
              </button>
            )}
            {isAdmin && (
              <button
                type="button"
                onClick={openSetup}
                className={`inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 sm:min-h-0 ${
                  setupProgress?.completed
                    ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {setupProgress?.completed
                  ? <CheckCircle2 className="h-4 w-4" />
                  : <ClipboardCheck className="h-4 w-4 text-primary-600 dark:text-primary-400" />}
                {setupChipLabel}
              </button>
            )}
            {busy && <RefreshCw className="h-4 w-4 animate-spin text-primary-600" aria-label="Processing" />}
            {isAdmin ? (
              <select
                value={study.status}
                onChange={(e) => setStatus(e.target.value)}
                aria-label="Study status"
                className={`rounded-lg border-0 px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 ${meta.badge}`}
              >
                {STUDY_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            ) : (
              <span className={`rounded-full px-3 py-1 text-sm font-medium ${meta.badge}`}>{meta.label}</span>
            )}
          </div>
        </div>

        <nav className="-mb-4 mt-4 flex gap-1 overflow-x-auto border-t border-gray-100 pt-2 dark:border-gray-700 sm:-mb-6" aria-label="Study sections">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setSearchParams({ tab: t.key }, { replace: true })}
              className={`whitespace-nowrap rounded-t-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150 min-h-[44px] sm:min-h-0 ${
                tab === t.key
                  ? 'border-b-2 border-primary-600 text-primary-700 dark:text-primary-400'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {unlinked && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800/60 dark:bg-amber-900/15">
          <Link2 className="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-amber-900 dark:text-amber-300">Link this study to eReg</p>
            <p className="text-xs text-amber-800/80 dark:text-amber-300/70">
              Study documents now live in eReg. Link the study so Call-Mode Q&A, AI generation, and criteria extraction read the current protocol/ICF.
            </p>
          </div>
          {isAdmin && (
            <button
              type="button"
              onClick={() => setLinkOpen(true)}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
            >
              <Link2 className="h-4 w-4" /> Link to eReg
            </button>
          )}
        </div>
      )}

      {staleItems.length > 0 && (
        <div className="flex flex-wrap items-start gap-3 rounded-xl border border-primary-200 bg-primary-50 p-4 dark:border-primary-800 dark:bg-primary-900/20">
          <RefreshCw className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-600 dark:text-primary-400" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-primary-900 dark:text-primary-300">
              A study document was updated in eReg
            </p>
            <p className="text-xs text-primary-800/80 dark:text-primary-300/70">
              Derived from the previous version: {staleItems.join(' · ')}. Regenerate each item when you're ready — nothing changes automatically.
            </p>
          </div>
        </div>
      )}

      {/* All tabs stay MOUNTED (visibility-toggled) so switching tabs never
          discards unsaved prescreen/draft edits held in tab-local state. */}
      <div className={tab === 'overview' ? '' : 'hidden'}>
        <OverviewTab detail={detail} isAdmin={isAdmin} onChanged={() => load(true)} />
      </div>
      <div className={tab === 'script' ? '' : 'hidden'}>
        <ScriptTab detail={detail} isAdmin={isAdmin} onChanged={() => load(true)} />
      </div>
      <div className={tab === 'prescreen' ? '' : 'hidden'}>
        <PrescreenTab detail={detail} isAdmin={isAdmin} onChanged={() => load(true)} />
        {/* AI Review Criteria — the study-side lifecycle for AI eligibility
            reviews (extract → draft review → confirm). Sits with the prescreen
            surfaces since both define the study's screening criteria. */}
        <div className="mt-4">
          <ReviewCriteriaPanel
            studyId={study.id}
            isAdmin={isAdmin}
            hasExtractedDoc={detail.documents.some((d) => d.extraction_status === 'complete')}
          />
        </div>
      </div>
      <div className={tab === 'forms' ? '' : 'hidden'}>
        <FormsTab detail={detail} onChanged={() => load(true)} />
      </div>
      <div className={tab === 'templates' ? '' : 'hidden'}>
        <TemplatesTab detail={detail} isAdmin={isAdmin} onChanged={() => load(true)} />
      </div>
      <div className={tab === 'flows' ? '' : 'hidden'}>
        <StudyFlowsTab detail={detail} isAdmin={isAdmin} onChanged={() => load(true)} />
      </div>
      <div className={tab === 'website' ? '' : 'hidden'}>
        <WebsiteTab detail={detail} isAdmin={isAdmin} onChanged={() => load(true)} />
      </div>
      <div className={tab === 'documents' ? '' : 'hidden'}>
        <DocumentsTab detail={detail} isAdmin={isAdmin} onChanged={() => load(true)} />
      </div>
      <div className={tab === 'recruiters' ? '' : 'hidden'}>
        <RecruitersTab detail={detail} isAdmin={isAdmin} onChanged={() => load(true)} />
      </div>
      <div className={tab === 'scheduling' ? '' : 'hidden'}>
        <ScheduleRequirementsTab studyId={study.id} isAdmin={isAdmin} />
      </div>
      {/* Performance is mounted ONLY when active (unlike the draft-bearing tabs
          above) so its analytics fetches don't fire in the background. */}
      {tab === 'performance' && <PerformanceTab studyId={study.id} />}

      {isAdmin && (
        <SetupChecklist
          studyId={study.id}
          open={setupOpen}
          onClose={closeSetup}
          onNavigateTab={gotoTab}
          onSetStatus={setStatus}
          onProgress={setSetupProgress}
          reloadKey={setupReloadKey}
        />
      )}

      {isAdmin && (
        <ImportLeadsModal
          study={{ slug: study.slug, title: studyInternalLabel(study), id: study.id }}
          open={importOpen}
          onClose={() => setImportOpen(false)}
          onImported={() => load(true)}
        />
      )}

      {isAdmin && linkOpen && (
        <LinkRegistryModal
          studyId={study.id}
          studyLabel={studyInternalLabel(study)}
          onClose={() => setLinkOpen(false)}
          onLinked={() => { setLinkOpen(false); load(true); }}
        />
      )}

      {isAdmin && (
        <EditStudyModal
          study={study}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSaved={(s) => {
            // The PATCH response row has no embedded `count`, so spread `...s`
            // over the existing study (keeps the lead-count chip), then refetch
            // to re-sync the count.
            setDetail((d) => (d ? { ...d, study: { ...d.study, ...s } } : d));
            setEditOpen(false);
            load(true);
          }}
        />
      )}
    </div>
  );
}
